import React, { useEffect, useRef, useState } from "react";

import { Building2, Check, ChevronDown, Users } from "lucide-react";

interface OrgSelectorProps {
  isExpanded: boolean;
}

interface Organization {
  id: string;
  name: string;
  members: number;
  selected?: boolean;
}

const organizations: Organization[] = [
  { id: "jpm", name: "JPMorgan Chase", members: 156, selected: true },
  { id: "chase", name: "Chase Digital", members: 42 },
  { id: "labs", name: "Retail Banking Labs", members: 18 },
];

export const OrgSelector: React.FC<OrgSelectorProps> = ({ isExpanded }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const activeOrg =
    organizations.find((org) => org.selected) ?? organizations[0];

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`rounded-[10px] border border-white/10 bg-[#141414] hover:bg-[#1A1A1A] transition-colors ${
          isExpanded
            ? "h-11 w-full px-3 flex items-center justify-between"
            : "h-10 w-full flex items-center justify-center"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-md bg-[hsl(var(--color-gold))]/15 border border-[hsl(var(--color-gold))]/30 flex items-center justify-center shrink-0">
            <Building2 size={14} className="text-[hsl(var(--color-gold))]" />
          </div>
          {isExpanded && (
            <span className="text-[16px] font-semibold text-white truncate">
              {activeOrg.name}
            </span>
          )}
        </div>
        {isExpanded && <ChevronDown size={14} className="text-white/50" />}
      </button>

      {open && (
        <div
          className={`absolute top-12 rounded-[10px] border border-white/10 bg-[#060606] shadow-2xl z-[60] ${
            isExpanded ? "left-0 w-full" : "left-0 w-64"
          }`}
        >
          <div className="px-3 py-2 text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
            Your Organizations
          </div>
          <div className="p-1.5">
            {organizations.map((org) => (
              <button
                key={org.id}
                type="button"
                className={`w-full rounded-md px-2.5 py-2 text-left transition-colors ${
                  org.selected
                    ? "bg-[#252525] border border-white/10"
                    : "hover:bg-white/[0.06] border border-transparent"
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 w-6 h-6 rounded-md flex items-center justify-center bg-[hsl(var(--color-gold))]/15 border border-[hsl(var(--color-gold))]/30">
                    <Building2
                      size={13}
                      className="text-[hsl(var(--color-gold))]"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-white truncate">
                      {org.name}
                    </div>
                    <div className="text-xs text-white/50 flex items-center gap-1 mt-0.5">
                      <Users size={11} />
                      <span>{org.members} members</span>
                    </div>
                  </div>
                  {org.selected && (
                    <Check
                      size={14}
                      className="text-[hsl(var(--color-gold))] mt-1"
                    />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
